import { Component, useMemo } from 'react';
import { formatDate } from '@/lib/localeFormatters';
import type { ElementType, ErrorInfo, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import { Bot, Search, Loader2, CheckCircle, AlertCircle, Clock, Ban } from 'lucide-react';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import type { AgentSessionDTO, AssignedSkillDTO } from '@/services/roros/agent-sessions.service';
import { DelegationCard } from './DelegationCard';
import { UseAsMenu } from './UseAsMenu';
import { ToolConfirmationCard } from './ToolConfirmationCard';

// ─── AC-CHAT-4: Inspector error boundary ─────────────────────────────────────
// Wraps InspectorTab so a render-time throw never unmounts the parent tab bar.
// The tab buttons live outside this boundary and remain fully interactive.
interface InspectorBoundaryState { hasError: boolean }
export class InspectorBoundary extends Component<{ children: ReactNode }, InspectorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): InspectorBoundaryState { return { hasError: true }; }
  componentDidCatch(err: Error, info: ErrorInfo): void {
    console.error('[InspectorBoundary] caught render error:', err, info);
  }
  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="p-4 text-sm text-red-500" data-testid="inspector-error">
          {t('Failed to load inspector data.')}
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChatMessage {
  id: string;
  role: 'user' | 'tool' | 'assistant';
  content: string;
  toolName?: string;
  toolInput?: Record<string, unknown> | null;
  toolResult?: Record<string, unknown> | null;
  pending?: boolean;
  invokedSkillId?: string | null;
}

// ─── Status chip ──────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; icon: ElementType; className: string }> = {
  idle:      { label: 'idle',      icon: Clock,        className: 'bg-slate-100 text-slate-600' },
  running:   { label: 'running',   icon: Loader2,      className: 'bg-blue-50 text-blue-700 animate-pulse' },
  completed: { label: 'completed', icon: CheckCircle,  className: 'bg-green-50 text-green-700' },
  failed:    { label: 'failed',    icon: AlertCircle,  className: 'bg-red-50 text-red-700' },
  cancelled: { label: 'cancelled', icon: Ban,          className: 'bg-amber-50 text-amber-700' },
};

export function StatusChip({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.idle;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}
      data-testid="session-status-chip"
    >
      <Icon className="h-3 w-3" />
      {t(cfg.label)}
    </span>
  );
}

// ─── Message bubble ────────────────────────────────────────────────────────────

interface MessageBubbleProps {
  msg: ChatMessage;
  sessionId: string;
  onPromoted?: () => void;
  onResolved?: () => void;
}

export function MessageBubble({ msg, sessionId, onResolved }: MessageBubbleProps) {
  if (msg.role === 'tool') {
    // AC-M6-6: render ToolConfirmationCard for write-tool confirmation requests
    if (msg.toolName === 'tool_confirmation_request') {
      const p = (msg.toolInput ?? {}) as Record<string, string>;
      return (
        <div className="py-1" data-testid="chat-tool-chunk">
          <ToolConfirmationCard
            sessionId={sessionId}
            confirmationId={p.confirmationId ?? ''}
            toolName={p.toolName ?? ''}
            connectionName={p.connectionName ?? ''}
            formattedDescription={p.formattedDescription ?? ''}
            expiresAt={p.expiresAt ?? new Date(Date.now() + 300_000).toISOString()}
            onResolved={onResolved}
          />
        </div>
      );
    }

    // AC-AGS-M4-4: render DelegationCard for delegate_task tool calls
    if (msg.toolName === 'delegate_task') {
      return (
        <div className="py-1" data-testid="chat-tool-chunk">
          <DelegationCard
            toolResult={msg.toolResult ?? null}
            toolInput={msg.toolInput ?? null}
            parentSessionId={sessionId}
          />
        </div>
      );
    }

    return (
      <div className="flex items-start gap-2 py-1" data-testid="chat-tool-chunk">
        <div className="flex-shrink-0 mt-0.5">
          <Search className="h-4 w-4 text-amber-500" />
        </div>
        <div className="text-xs text-slate-500 italic bg-amber-50 rounded px-2 py-1 border border-amber-100 max-w-prose">
          <span className="font-semibold text-amber-700">{t('Tool')}: {msg.toolName ?? 'knowledge_search'}</span>
          {msg.content && <div className="mt-0.5 whitespace-pre-wrap">{msg.content}</div>}
          {msg.pending && <span className="ml-1 text-amber-400">{t('…')}</span>}
        </div>
      </div>
    );
  }

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end" data-testid="chat-user-message">
        <div className="max-w-prose bg-blue-600 text-white rounded-2xl rounded-tr-sm px-4 py-2 text-sm">
          {msg.content}
          {msg.invokedSkillId && (
            <span className="ml-2 text-xs text-blue-200 italic" data-testid="skill-marker">⚡ {t('skill')}</span>
          )}
        </div>
      </div>
    );
  }

  // assistant — AC-CHAT-11: render markdown via react-markdown (prose prose-sm, no rehype-raw).
  // AC-M5-7: wrap in group so the ⋮ button shows on hover.
  return (
    <div className="flex items-start gap-2 group" data-testid="chat-assistant-message">
      <div className="flex-shrink-0 mt-1">
        <Bot className="h-5 w-5 text-purple-500" />
      </div>
      <div className="max-w-prose bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-2 text-sm text-slate-800">
        {msg.pending && !msg.content
          ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          : (
            <div className="text-sm leading-relaxed [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:my-1.5 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:my-1.5 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:my-1 [&_blockquote]:border-l-4 [&_blockquote]:border-slate-300 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-slate-600 [&_blockquote]:my-1 [&_hr]:my-2 [&_hr]:border-slate-200 [&_a]:text-blue-600 [&_a]:underline [&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_pre]:my-1 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-slate-100 [&_pre]:p-2 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_table]:my-1 [&_th]:border [&_th]:border-slate-300 [&_th]:px-2 [&_th]:py-0.5 [&_td]:border [&_td]:border-slate-300 [&_td]:px-2 [&_td]:py-0.5">
              <ReactMarkdown
                urlTransform={(url: string) => {
                  // AC-CHAT-11 XSS: only allow http/https/mailto and relative URLs
                  if (/^(https?:|mailto:|\/|#)/.test(url)) return url;
                  return '';
                }}
                components={{
                  a: ({ href, children, ...props }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
                  ),
                }}
              >{msg.content}</ReactMarkdown>
            </div>
          )
        }
      </div>
      {/* AC-M5-7: "Use as…" ⋮ menu — only on persisted (non-pending) assistant messages */}
      {!msg.pending && msg.content && (
        <div className="flex-shrink-0 mt-1">
          <UseAsMenu sessionId={sessionId} messageId={msg.id} />
        </div>
      )}
    </div>
  );
}

// ─── Skill autocomplete dropdown ──────────────────────────────────────────────

export function SkillAutocomplete({
  skills,
  query,
  onSelect,
}: {
  skills: AssignedSkillDTO[];
  query: string;
  onSelect: (name: string) => void;
}) {
  const filtered = useMemo(() => {
    if (!query) return skills;
    const q = query.toLowerCase();
    return skills.filter(
      (s) => s.name.toLowerCase().includes(q) || s.displayName.toLowerCase().includes(q),
    );
  }, [skills, query]);

  if (filtered.length === 0) {
    return (
      <div
        className="absolute bottom-full mb-1 left-0 w-72 bg-white border border-slate-200 rounded-lg shadow-md p-3 text-sm text-slate-400 z-50"
        data-testid="skill-autocomplete-empty"
      >
        {t('No skills assigned')}
      </div>
    );
  }

  return (
    <div
      className="absolute bottom-full mb-1 left-0 w-72 bg-white border border-slate-200 rounded-lg shadow-md overflow-hidden z-50"
      data-testid="skill-autocomplete"
    >
      <div className="px-3 py-2 border-b border-slate-100 text-xs text-slate-400 font-medium uppercase tracking-wider">
        {t('Assigned Skills')}
      </div>
      {filtered.map((skill) => (
        <Button
          key={skill.name}
          variant="ghost"
          className="w-full text-left px-3 py-2 justify-start h-auto rounded-none hover:bg-blue-50 focus:bg-blue-50"
          onClick={() => onSelect(skill.name)}
          data-testid={`skill-option-${skill.name}`}
        >
          <div>
            <div className="font-medium text-sm text-slate-800">/{skill.name}</div>
            {skill.description && (
              <div className="text-xs text-slate-500 truncate">{skill.description}</div>
            )}
          </div>
        </Button>
      ))}
    </div>
  );
}

// ─── Session list item ─────────────────────────────────────────────────────────

export function SessionListItem({
  session,
  isActive,
  onClick,
}: {
  session: AgentSessionDTO;
  isActive: boolean;
  onClick: () => void;
}) {
  const title = session.title ?? t('Agent Chat');
  const date = session.lastActivityAt
    ? formatDate(session.lastActivityAt)
    : formatDate(session.createdAt);

  return (
    <Button
      variant="ghost"
      className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors h-auto justify-start ${
        isActive
          ? 'bg-blue-50 border border-blue-200'
          : 'hover:bg-slate-50 border border-transparent'
      }`}
      onClick={onClick}
      data-testid="session-list-item"
    >
      <div className="w-full">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <span className="text-sm font-medium text-slate-800 truncate">{title}</span>
          <StatusChip status={session.status ?? 'idle'} />
        </div>
        <div className="text-xs text-slate-400 mt-0.5">{date}</div>
      </div>
    </Button>
  );
}
